/*
 * <BeLink> -- an inline link. Thin, like the other runs: a class name and the
 * choice of element. Behaviour and rationale live in components/link.css.
 *
 * `href` renders an <a>; anything else renders a <button type="button">, which
 * is the honest element for a link that moves app state rather than the
 * document; link.css carries its own UA reset for that form.
 */

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { type BeCommon, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";

export type BeLinkProps = BeCommon &
	Omit<AnchorHTMLAttributes<HTMLElement> & ButtonHTMLAttributes<HTMLElement>, "className" | "style" | "type"> & {
		children?: ReactNode;
		/** Renders an <a>. Omit for the <button> form. */
		href?: string;
		/** White ink + the light AA ramp, for a link on the desktop teal. */
		light?: boolean;
		/** Yield and ellipsize rather than push the row wider; a plain-string child
		 * becomes the `title`, so the clipped text stays readable. */
		truncate?: boolean;
	};

export const BeLink = beComponent<BeLinkProps, HTMLElement>("BeLink", (props, be) => {
	const { children, href, light, truncate, scale, className, style, ...rest } = props;
	// shrink AND truncate: a row child keeps its own size by default (stack.css),
	// so clipping alone would never get the chance to apply.
	const root = be("be-link", light && "is-light", truncate && "be-shrink", truncate && "be-truncate");
	// Clipping text without saying what was clipped is the half-done version, so
	// the prop carries both. Before ...rest, so an explicit title still wins.
	const title = truncate && typeof children === "string" ? children : undefined;
	const content = pixelText(children);

	return href != null ? (
		<a title={title} {...rest} {...root} href={href}>
			{content}
		</a>
	) : (
		<button title={title} {...rest} {...root} type="button" className={root.className}>
			{content}
		</button>
	);
});
